import {
  OIDC_CONSTANTS,
  TRIPLA_OPEN_ID_SIGN_IN_LOCALE,
} from 'core/auth/constants'
import isClientSessionExpired from 'core/auth/isClientSessionExpired'
import requestClientSession from 'core/auth/requestClientSession'
import triplaOpenIdSignIn from 'core/auth/triplaOpenIdSignIn'
import dayjs from 'lib/dayjs'
import { useCallback, useMemo } from 'react'
import { signIn, signOut, useSession } from 'next-auth/react'
import { useParams } from 'next/navigation'
import {
  CLIENT_SESSION_KEY,
  SMARTCLUB_REQUEST_MAX_RETRY,
  TRIPLA_REQUEST_MAX_RETRY,
} from 'utils/constants'
import calculateRetryDelay from 'utils/calculateRetryDelay'
import { getLogger } from 'tokyu-hooks'

export const LOG_IN_CALLBACK_CURRENT_URL = 'log_in_call_back_current_url'

interface AuthenticationState {
  isAuthenticated: boolean
  isLoading: boolean
  data: ReturnType<typeof useSession>['data']
  getTriplaClientSession: () => Promise<string>
  withTriplaApiRequest: <P extends unknown[], R = unknown>(
    fn: (
      clientSession: string,
      accessToken?: string,
    ) => (...args: P) => Promise<R>,
  ) => (...args: P) => Promise<R | null>
  withSmartclubApiRequest: <P extends unknown[], R = unknown>(
    fn: (
      smartClubId?: number,
      accessToken?: string,
    ) => (...args: P) => Promise<R>,
  ) => (...args: P) => Promise<R | null>
  withCombinedApiRequest: <P extends unknown[], R = unknown>(
    fn: (
      clientSession: string,
      triplaAccessToken?: string,
      smartClubId?: number,
      smartclubAccessToken?: string,
    ) => (...args: P) => Promise<R>,
  ) => (...args: P) => Promise<R | null>
  updateTriplaAccessToken: (triplaAccessToken: string) => Promise<any>
  resetErrorState: () => Promise<any>
  getTriplaAccessTokenAndClientSession: () => Promise<{
    triplaAccessToken: string | undefined
    clientSession: string
  }>
  enhancedTriplaOpenIdSignIn: (
    clientSession: string,
    openIdToken: string,
    retryCount?: number,
  ) => Promise<string>
  login: (options?: { callbackUrl?: string; prompt?: string }) => Promise<void>
  logout: (options?: {
    redirect?: boolean
    callbackUrl?: string
  }) => Promise<any>
}

const useAuthentication = (): AuthenticationState => {
  const params = useParams<{ locale: string }>()
  const { locale } = params || {}

  const { status, data: originalData, update } = useSession()

  const isAuthenticatedMemoized = useMemo(
    () =>
      Boolean(originalData?.triplaAccessToken) &&
      Boolean(originalData?.smartclubAccessToken) &&
      status === 'authenticated',
    [
      originalData?.triplaAccessToken,
      originalData?.smartclubAccessToken,
      status,
    ],
  )

  const isLoadingMemoized = useMemo(() => status === 'loading', [status])

  // Memoize specific used session data to prevent unnecessary re-renders
  // used for internal usage inside this hooks
  const memoizedData = useMemo(
    () => originalData,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      originalData?.smartClubId,
      originalData?.smartclubAccessToken,
      originalData?.smartclubAccessTokenExpirationDate,
      originalData?.canRefreshSmartClub,
      originalData?.triplaAccessToken,
      originalData?.triplaAccessTokenExpirationDate,
    ],
  )

  const hasData = !!memoizedData

  // Memoize session-related functions to reduce re-renders
  const sessionManager = useCallback(() => {
    // Access tokens with integrity check built-in
    const getSmartClubAccessToken = async () => {
      if (status !== 'authenticated' || !hasData) {
        return undefined
      }

      const {
        smartclubAccessToken,
        canRefreshSmartClub,
        smartclubAccessTokenExpirationDate,
        smartClubId,
      } = memoizedData

      if (dayjs(smartclubAccessTokenExpirationDate).isAfter(dayjs())) {
        return smartclubAccessToken
      }

      if (smartClubId && canRefreshSmartClub) {
        const newSession = await update({ shouldRefreshSmartClubToken: true })
        return newSession?.smartclubAccessToken
      }

      return undefined
    }

    const getTriplaAccessToken = async () => {
      if (status !== 'authenticated' || !hasData) {
        return undefined
      }

      const {
        triplaAccessToken,
        triplaAccessTokenExpirationDate,
        smartclubAccessToken,
      } = memoizedData

      if (dayjs(triplaAccessTokenExpirationDate).isAfter(dayjs())) {
        return triplaAccessToken
      }

      if (smartclubAccessToken) {
        // Check if smartclub access token is expired, otherwise logout
        // Smartclub access token is required to get new tripla access token
        const smartclubAccessToken = await getSmartClubAccessToken()
        if (!smartclubAccessToken) {
          await signOut({ redirect: true, callbackUrl: `/${locale}` })
          return undefined
        }

        const newSession = await update({ shouldRefreshTriplaToken: true })
        return newSession?.triplaAccessToken
      }

      return undefined
    }

    return {
      getSmartClubAccessToken,
      getTriplaAccessToken,
    }
  }, [status, hasData, memoizedData, locale, update])

  // Extract the session manager functions to use throughout the hook
  const { getSmartClubAccessToken, getTriplaAccessToken } = useMemo(
    () => sessionManager(),
    [sessionManager],
  )

  const getTriplaClientSession = useCallback(async () => {
    const clientSession = localStorage.getItem(CLIENT_SESSION_KEY)
    if (!clientSession || isClientSessionExpired(clientSession)) {
      const newClientSession = await requestClientSession()
      localStorage.setItem(CLIENT_SESSION_KEY, newClientSession)

      return newClientSession
    }

    return clientSession
  }, [])

  const refreshTriplaToken = useCallback(
    () => update({ shouldRefreshTriplaToken: true }),
    [update],
  )

  const refreshSmartClubToken = useCallback(
    () => update({ shouldRefreshSmartClubToken: true }),
    [update],
  )

  const updateTriplaAccessToken = useCallback(
    (triplaAccessToken: string) => update({ triplaAccessToken }),
    [update],
  )

  const resetErrorState = useCallback(
    () => update({ resetErrorState: true }),
    [update],
  )

  const login = useCallback(
    async ({
      callbackUrl,
      prompt,
    }: { callbackUrl?: string; prompt?: string } = {}) => {
      await signIn(
        OIDC_CONSTANTS.PROVIDER_ID,
        {
          callbackUrl:
            callbackUrl === LOG_IN_CALLBACK_CURRENT_URL
              ? undefined
              : callbackUrl || `/${locale}`,
        },
        {
          prompt,
          register_route: 'stay',
        } as Record<string, any>,
      )
    },
    [locale],
  )

  const logout = useCallback(
    async ({
      redirect,
      callbackUrl,
    }: { redirect?: boolean; callbackUrl?: string } = {}) => {
      return await signOut({
        redirect: redirect,
        callbackUrl: callbackUrl || `/${locale}`,
      })
    },
    [locale],
  )

  // Enhanced version of triplaOpenIdSignIn with error handling
  // Similar to tokyu-app's getAccessTokenFromOidApi function
  const enhancedTriplaOpenIdSignIn = useCallback(
    async (
      clientSession: string,
      openIdToken: string,
      retryCount = 0,
    ): Promise<string> => {
      try {
        // Call the original triplaOpenIdSignIn function
        const response = await triplaOpenIdSignIn({
          clientSession,
          locale: TRIPLA_OPEN_ID_SIGN_IN_LOCALE,
          openIdToken,
        })

        await updateTriplaAccessToken(response.data.access_token)

        return response.data.access_token
      } catch (err: any) {
        const errResponse = err?.response?.data?.errors
        const errorTitle = errResponse?.[0]?.title

        // Check if error is related to incorrect OpenID token
        if (
          errorTitle?.includes('Open ID token') &&
          errorTitle?.includes('incorrect') &&
          retryCount < TRIPLA_REQUEST_MAX_RETRY &&
          memoizedData?.canRefreshSmartClub
        ) {
          try {
            // Calculate and apply delay before retrying
            const delay = calculateRetryDelay(retryCount)
            await new Promise((resolve) => setTimeout(resolve, delay))

            // Get new SmartClub access token using the refresh token
            const newSession = await refreshSmartClubToken()
            if (!newSession) {
              getLogger().error('newSession is null')
              throw err
            }

            // Retry with the new token
            return enhancedTriplaOpenIdSignIn(
              clientSession,
              newSession.smartclubAccessToken as string,
              retryCount + 1,
            )
          } catch (refreshError) {
            getLogger().error('Error refreshing SmartClub token', refreshError)
            throw err // Re-throw the original error
          }
        }

        getLogger().error('Error in triplaOpenIdSignIn', err)
        throw err
      }
    },
    [
      memoizedData?.canRefreshSmartClub,
      refreshSmartClubToken,
      updateTriplaAccessToken,
    ],
  )

  const excuteTriplaFn = useCallback(
    async <P extends unknown[], R = unknown>(
      fn: (
        clientSession: string,
        accessToken?: string,
      ) => (...args: P) => Promise<R>,
      args: P,
      retry: number,
      clientSession: string,
      accessToken?: string,
    ): Promise<R | null> => {
      try {
        return await fn(clientSession, accessToken)(...args)
      } catch (err: any) {
        const errorsStatus = err?.response?.status
        if (errorsStatus === 401 && hasData) {
          if (retry < TRIPLA_REQUEST_MAX_RETRY) {
            // Calculate and apply delay before retrying
            const delay = calculateRetryDelay(retry)
            await new Promise((resolve) => setTimeout(resolve, delay))

            const newClientSession = await requestClientSession()
            const newSession = await refreshTriplaToken()
            if (!newSession) {
              getLogger().error('newSession is null')
              logout()
              return null
            }

            return await excuteTriplaFn(
              fn,
              args,
              retry + 1,
              newClientSession,
              newSession.triplaAccessToken,
            )
          } else {
            logout()
          }
        }

        throw err
      }
    },
    [hasData, refreshTriplaToken, logout],
  )

  const excuteSmartclubFn = useCallback(
    async <P extends unknown[], R = unknown>(
      fn: (
        smartClubId?: number,
        accessToken?: string,
      ) => (...args: P) => Promise<R>,
      args: P,
      retry: number,
      smartClubId?: number,
      accessToken?: string,
    ): Promise<R | null> => {
      try {
        return await fn(smartClubId, accessToken)(...args)
      } catch (err: any) {
        const errorsStatus = err?.response?.status
        if (errorsStatus === 401 && hasData) {
          if (retry < SMARTCLUB_REQUEST_MAX_RETRY) {
            // Calculate and apply delay before retrying
            const delay = calculateRetryDelay(retry)
            await new Promise((resolve) => setTimeout(resolve, delay))

            if (
              !memoizedData?.smartClubId ||
              !memoizedData?.canRefreshSmartClub
            ) {
              logout()
              return null
            }

            const newSession = await refreshSmartClubToken()
            if (!newSession) {
              logout()
              return null
            }

            return await excuteSmartclubFn(
              fn,
              args,
              retry + 1,
              smartClubId,
              newSession.smartclubAccessToken,
            )
          } else {
            logout()
          }
        }

        throw err
      }
    },
    [
      hasData,
      memoizedData?.smartClubId,
      memoizedData?.canRefreshSmartClub,
      refreshSmartClubToken,
      logout,
    ],
  )

  const withTriplaApiRequest = useCallback(
    <P extends unknown[], R = unknown>(
      fn: (
        clientSession: string,
        accessToken?: string,
      ) => (...args: P) => Promise<R>,
    ) =>
      async (...args: P): Promise<R | null> => {
        const clientSession = await getTriplaClientSession()
        const triplaAccessToken = await getTriplaAccessToken()

        return await excuteTriplaFn(
          fn,
          args,
          0,
          clientSession,
          triplaAccessToken,
        )
      },
    [getTriplaClientSession, getTriplaAccessToken, excuteTriplaFn],
  )

  const withSmartclubApiRequest = useCallback(
    <P extends unknown[], R = unknown>(
      fn: (
        smartClubId?: number,
        accessToken?: string,
      ) => (...args: P) => Promise<R>,
    ) =>
      async (...args: P): Promise<R | null> => {
        const smartclubAccessToken = await getSmartClubAccessToken()

        return await excuteSmartclubFn(
          fn,
          args,
          0,
          memoizedData?.smartClubId,
          smartclubAccessToken,
        )
      },
    [memoizedData?.smartClubId, getSmartClubAccessToken, excuteSmartclubFn],
  )

  const getTriplaAccessTokenAndClientSession = useCallback(async () => {
    const clientSession = await getTriplaClientSession()
    const triplaAccessToken = await getTriplaAccessToken()

    return { triplaAccessToken, clientSession }
  }, [getTriplaClientSession, getTriplaAccessToken])

  const executeTriplaAndSmartClubFn = useCallback(
    async <P extends unknown[], R = unknown>(
      fn: (
        clientSession: string,
        triplaAccessToken?: string,
        smartClubId?: number,
        smartclubAccessToken?: string,
      ) => (...args: P) => Promise<R>,
      args: P,
      retry: number,
      clientSession: string,
      triplaAccessToken?: string,
      smartClubId?: number,
      smartclubAccessToken?: string,
    ): Promise<R | null> => {
      try {
        return await fn(
          clientSession,
          triplaAccessToken,
          smartClubId,
          smartclubAccessToken,
        )(...args)
      } catch (err: any) {
        const errorsStatus = err?.response?.status
        if (errorsStatus === 401 && hasData) {
          if (
            retry <
            Math.max(TRIPLA_REQUEST_MAX_RETRY, SMARTCLUB_REQUEST_MAX_RETRY)
          ) {
            // Calculate and apply delay before retrying
            const delay = calculateRetryDelay(retry)
            await new Promise((resolve) => setTimeout(resolve, delay))

            // First try refreshing SmartClub token
            if (
              !memoizedData?.smartClubId ||
              !memoizedData?.canRefreshSmartClub
            ) {
              logout()
              return null
            }

            const newSmartClubSession = await refreshSmartClubToken()
            if (!newSmartClubSession) {
              logout()
              return null
            }

            // Then refresh Tripla token using the new SmartClub token
            const newClientSession = await requestClientSession()
            const newTriplaSession = await refreshTriplaToken()
            if (!newTriplaSession) {
              logout()
              return null
            }

            // Retry with new tokens
            return await executeTriplaAndSmartClubFn(
              fn,
              args,
              retry + 1,
              newClientSession,
              newTriplaSession.triplaAccessToken,
              smartClubId,
              newSmartClubSession.smartclubAccessToken,
            )
          } else {
            logout()
          }
        }

        throw err
      }
    },
    [
      hasData,
      memoizedData?.smartClubId,
      memoizedData?.canRefreshSmartClub,
      refreshSmartClubToken,
      refreshTriplaToken,
      logout,
    ],
  )

  const withCombinedApiRequest = useCallback(
    <P extends unknown[], R = unknown>(
      fn: (
        clientSession: string,
        triplaAccessToken?: string,
        smartClubId?: number,
        smartclubAccessToken?: string,
      ) => (...args: P) => Promise<R>,
    ) =>
      async (...args: P): Promise<R | null> => {
        // Get Tripla tokens
        const clientSession = await getTriplaClientSession()
        const triplaAccessToken = await getTriplaAccessToken()

        // Get SmartClub tokens
        const smartclubAccessToken = await getSmartClubAccessToken()

        if (!triplaAccessToken || !smartclubAccessToken) {
          // If either token is missing, authentication is incomplete
          if (status === 'authenticated') {
            // Only logout if we're supposed to be authenticated
            logout()
          }
          return null
        }

        return await executeTriplaAndSmartClubFn(
          fn,
          args,
          0,
          clientSession,
          triplaAccessToken,
          memoizedData?.smartClubId,
          smartclubAccessToken,
        )
      },
    [
      status,
      memoizedData?.smartClubId,
      getTriplaClientSession,
      getTriplaAccessToken,
      getSmartClubAccessToken,
      executeTriplaAndSmartClubFn,
      logout,
    ],
  )

  return {
    isAuthenticated: isAuthenticatedMemoized,
    isLoading: isLoadingMemoized,
    data: originalData,
    getTriplaClientSession,
    withTriplaApiRequest,
    withSmartclubApiRequest,
    withCombinedApiRequest,
    updateTriplaAccessToken,
    resetErrorState,
    getTriplaAccessTokenAndClientSession,
    enhancedTriplaOpenIdSignIn,
    login,
    logout,
  }
}

export default useAuthentication
